import { specialSlugs } from '@aboutyou/legacy-shared/src/localizedUrls/specialSlugs'
import LinkToUrl from 'components/LinkToUrl'
import { useTranslation } from 'helpers/hooks/useTranslation'
import { useObserver } from 'mobx-react-lite'
import React, { FC } from 'react'
import { useConfigStore, useLocaleStore } from 'store/context'
import styled, { css } from 'styled-components'
import Icon, { IconSize } from 'uiComponents/atoms/Icon/Icon'
import { IconName } from 'uiComponents/atoms/Icon/IconName'
import {
  backgroundColorInactive,
  primaryColor,
} from 'uiComponents/constants/colors'
import { fsXXS, fsXXXS, fwBold } from 'uiComponents/constants/typography'
import { uspLinks } from '../../../../../../../shared/src/config/usps'

const PromiseIcon: FC<{ label: string; icon: string; isSmall?: boolean }> = ({
  label,
  icon,
  isSmall,
}) => {
  const iconNameFromOldPromise = `Ic${icon[0].toUpperCase()}${icon.slice(1)}`

  return (
    <PromiseIconHolder>
      <StyledIcon
        data-testid="PromiseBarIcon"
        size={IconSize.S}
        // @ts-ignore
        asset={IconName[iconNameFromOldPromise]}
        isSmall={isSmall}
      />
      <PromiseIconLabel isSmall={isSmall}>{label}</PromiseIconLabel>
    </PromiseIconHolder>
  )
}

export const PromiseBar: FC<{ isSmall?: boolean }> = ({ isSmall }) => {
  const { t } = useTranslation()
  const locale = useLocaleStore(store => store.locale)
  const configStore = useConfigStore()
  const slugs = specialSlugs(locale)
  const shippingAndDeliveryUrl = slugs.shippingAndDelivery

  const promises = useObserver(() => configStore.promiseBarPromises(t))
  // There is not a link for every locale, so we have this fallback in place
  // or else the user will end up on the home-page
  const fallbackUrl = `/h/${shippingAndDeliveryUrl}-360003913400`
  const url = uspLinks[locale] ? uspLinks[locale] : fallbackUrl

  if (!promises.length) {
    return null
  }

  return (
    <DeinShopPromiseBarContainer
      data-testid="DeinShopPromiseBar"
      isSmall={isSmall}
    >
      {promises.map(promise => (
        <li key={promise.label}>
          <StyledLink target={'_blank'} to={url}>
            <PromiseIcon
              label={promise.label}
              icon={promise.icon}
              isSmall={isSmall}
            />
          </StyledLink>
        </li>
      ))}
    </DeinShopPromiseBarContainer>
  )
}

const DeinShopPromiseBarContainer = styled.ul<{ isSmall?: boolean }>`
  display: flex;
  justify-content: space-around;
  background-color: ${backgroundColorInactive};
  padding: ${({ isSmall }) => (isSmall ? '10px' : '16px')} 40px;
  ${({ isSmall }) =>
    isSmall
      ? css`
          margin-top: 15px;
        `
      : css`
          margin: 20px auto;
          min-height: 60px;
          border-radius: 4px;
        `};
`

const StyledLink = styled(LinkToUrl)`
  position: relative;
  display: flex;
  align-items: center;
`

const PromiseIconHolder = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
`

const PromiseIconLabel = styled.span<{ isSmall?: boolean }>`
  font-size: ${({ isSmall }) => (isSmall ? fsXXXS : fsXXS)}px;
  ${fwBold};
  color: ${primaryColor};
`

const StyledIcon = styled(Icon)<{ isSmall?: boolean }>`
  margin-right: ${({ isSmall }) => (isSmall ? '8' : '4')}px;
  ${({ isSmall }) =>
    isSmall &&
    css`
      width: 18px;
      height: 18px;
      flex: 0 0 18px;
    `};
`
